import {useMap, Marker, Popup} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-routing-machine';
import {createRef, useCallback, useEffect, useRef, useState} from 'react';
import './style.css';
import {Order, RouteStatus} from 'types';
import useStore from '../../store';
import {get, isString} from 'lodash';
import {addressToString} from 'helpers/address';
import truck_icon from '../../icons/driver_truck.svg';
import {Popup as LeafletPopup} from 'leaflet';
import useDriverTracking from './useDriverTracking';

const returnIcon = (
  index: number | string = 1,
  isHistory?: boolean,
  canceled?: boolean
) => {
  const isStart = index === 0;
  const displayText = isStart ? '' : index;

  return new L.DivIcon({
    className: 'leaflet-div-icon',
    html: `<div class="my-div-icon ${isStart ? 'start' : ''} ${
      isHistory && !isStart ? 'completed_icon' : ''
    } ${canceled ? 'canceled' : ''}">${displayText}</div>`,
  });
};

const returnDriverIcon = () => {
  return new L.DivIcon({
    className: 'leaflet-div-icon-driver',
    html: `<div class="my-div-icon-driver"><img src="${truck_icon}"/></div>`,
  });
};

type Props = {
  display: boolean;
  orders: Order[];
  routeType?: 'car' | 'bike';
  isHistory?: boolean;
  start_location: {
    name: string;
    lat: number;
    lng: number;
  };
  end_location: {
    name: string;
    lat: number;
    lng: number;
  };
  markerClick?: (id: number) => void;
  status?: RouteStatus;
  driver?: {
    latitude: number;
    longitude: number;
  };
  driver_id?: number;
  openedPopup?: {
    index: number;
    id: number;
  };
};

const RoutingMachine = ({
  display,
  // driver,
  orders,
  start_location,
  end_location,
  routeType = 'car',
  status,
  driver_id,
  openedPopup,
  markerClick,
}: Props) => {
  const {location} = useDriverTracking(driver_id);
  const map = useMap();
  const directionRef = useRef<any>(null);
  const markerPopupRefs = useRef<{[id: number]: React.RefObject<LeafletPopup>}>(
    {}
  );
  const setDistance = useStore(useCallback((state) => state.setDistance, []));
  const [points, setPoints] = useState<any[]>([]);
  // Helper function to group orders by location
  const groupOrdersByLocation = useCallback((orders: Order[]) => {
    const locationGroups: {[key: string]: Order[]} = {};

    orders.forEach((order, index) => {
      const lat = get(order, 'delivery_address.latitude', 0);
      const lng = get(order, 'delivery_address.longitude', 0);
      const locationKey = `${lat},${lng}`;

      if (!locationGroups[locationKey]) {
        locationGroups[locationKey] = [];
      }
      locationGroups[locationKey].push({...order, countNumber: index + 1});
    });

    return locationGroups;
  }, []);

  const setupDirections = useCallback(
    async (orders: Order[]) => {
      if (orders.length < 1 || !start_location.name) {
        setDistance(0);
        return;
      }

      const points: any[] = [
        start_location,
        ...orders.map((item) => ({
          ...item,
          lat: get(item, 'delivery_address.latitude', 0),
          lng: get(item, 'delivery_address.longitude', 0),
          name: isString(item.delivery_address)
            ? item.delivery_address
            : addressToString(item.delivery_address) +
              (item.unit_address ? `—${item.unit_address}` : ''),
        })),
        end_location,
      ];

      let distance = 0;

      points.forEach((item, index) => {
        if (index < points.length - 1) {
          distance += map.distance(
            [points[index].lat, points[index].lng],
            [points[index + 1].lat, points[index + 1].lng]
          );
        } else {
          distance += map.distance(
            [points[index].lat, points[index].lng],
            [points[0].lat, points[0].lng]
          );
        }
      });

      setDistance(distance);

      setPoints(points);

      const route = routeType === 'bike' ? 'routed-bike' : 'routed-car';
      if (status === 'cancelled') return;
      //@ts-ignore
      directionRef.current = L.Routing.control({
        waypoints: points
          .filter((item) => !item.is_canceled)
          .map((item) => L.latLng(item.lat, item.lng)),
        lineOptions: {
          styles: [
            {
              color: '#0072b3',
              opacity: 0.8,
              weight: 4,
            },
          ],
        },
        createMarker: function () {
          return null;
        },
        addWaypoints: false,
        draggableWaypoints: false,
        // @ts-ignore
        router: L.Routing.osrmv1({
          serviceUrl: `https://routing.openstreetmap.de/${route}/route/v1`,
        }),
      }).addTo(map);
    },
    [map, start_location, end_location, routeType, setDistance, status]
  );
  useEffect(() => {
    if (!map) return;
    if (directionRef.current) {
      try {
        map.removeControl(directionRef.current);
        directionRef.current.hide();
      } catch (error) {}
      directionRef.current = null;
    }

    if (display && !directionRef.current) {
      setupDirections(orders);
    } else {
      if (directionRef.current && map?.hasLayer(directionRef.current)) {
        try {
          map?.removeControl(directionRef.current);
          directionRef.current.hide();
          directionRef.current = null;
        } catch (error) {}
      }
    }
  }, [setupDirections, display, orders, map, status]);

  useEffect(() => {
    if (!!openedPopup?.id) {
      const ref = markerPopupRefs.current[openedPopup.id];
      if (ref && ref.current) {
        ref.current.openOn(map);
      }
    }
  }, [openedPopup, map]);
  if (!display || !start_location.name) {
    return null;
  }
  // Group orders by location for markers
  const locationGroups = groupOrdersByLocation(
    points.slice(1, -1) // Remove start and end locations
  );
  // Create unique markers array
  const uniqueMarkers = Object.entries(locationGroups).map(
    ([locationKey, ordersAtLocation]) => {
      const [lat, lng] = locationKey.split(',').map(Number);
      const orderCount = ordersAtLocation.length;

      // markerLabel hisoblash
      let markerLabel;
      if (orderCount === 1) {
        markerLabel = String(ordersAtLocation[0].countNumber);
      } else if (orderCount === 2) {
        markerLabel = ordersAtLocation
          .map((order) => order.countNumber)
          .join('/');
      } else {
        markerLabel =
          ordersAtLocation
            .slice(0, 2)
            .map((order) => order.countNumber)
            .join('/') + '..';
      }

      return {
        lat,
        lng,
        orders: ordersAtLocation,
        label: markerLabel,
        hasMultiple: orderCount > 1,
        firstOrder: ordersAtLocation[0],
      };
    }
  );

  return (
    <>
      {location?.latitude && location?.longitude && (
        <Marker
          icon={returnDriverIcon()}
          position={[location?.latitude, location?.longitude]}
        ></Marker>
      )}
      {/* Start location marker */}
      {points.length > 0 && (
        <Marker
          key="start-marker"
          icon={returnIcon(0)}
          position={[points[0].lat, points[0].lng]}
        ></Marker>
      )}
      {/* End location marker */}
      {points.length > 0 && (
        <Marker
          key="end-marker"
          icon={returnIcon(0)}
          position={[
            points[points.length - 1].lat,
            points[points.length - 1].lng,
          ]}
        ></Marker>
      )}
      {uniqueMarkers.map((marker, index) => {
        if (!markerPopupRefs.current[marker.firstOrder.id]) {
          markerPopupRefs.current[marker.firstOrder.id] =
            createRef<LeafletPopup>();
        }

        return (
          <Marker
            key={`${marker.lat}-${marker.lng}-${index}`}
            icon={returnIcon(
              marker.label,
              marker.firstOrder.status === 'completed',
              !!marker.firstOrder.is_canceled
            )}
            eventHandlers={{
              click: () => {
                markerClick && markerClick(marker.firstOrder.id);
              },
            }}
            position={[marker.lat, marker.lng]}
          >
            <Popup ref={markerPopupRefs.current[marker.firstOrder.id]}>
              <div style={{display: 'flex', flexDirection: 'column', gap: 5}}>
                {marker.hasMultiple ? (
                  <>
                    {marker.orders.map((order: Order, orderIdx: number) => (
                      <div
                        key={orderIdx}
                        style={{
                          borderBottom:
                            orderIdx < marker.orders.length - 1
                              ? '1px solid #eee'
                              : 'none',
                          paddingBottom:
                            orderIdx < marker.orders.length - 1 ? 5 : 0,
                          marginBottom:
                            orderIdx < marker.orders.length - 1 ? 5 : 0,
                        }}
                      >
                        <span style={{fontWeight: 'bold'}}>
                          Stop {order.countNumber}
                        </span>
                        <br />
                        <span>
                          Patient -{' '}
                          {get(order, 'customer.user.first_name', 'N/A')}{' '}
                          {get(order, 'customer.user.last_name', 'N/A')}
                        </span>
                        <br />
                        <span>Address - {marker.firstOrder.name}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <span>
                      Patient -{' '}
                      {get(
                        marker.firstOrder,
                        'customer.user.first_name',
                        'N/A'
                      )}{' '}
                      {get(marker.firstOrder, 'customer.user.last_name', 'N/A')}{' '}
                      ({marker.label})
                    </span>
                    <span>Address - {marker.firstOrder.name}</span>
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};
export default RoutingMachine;
